import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Youtube, BookOpen, ExternalLink, RefreshCw, Loader2, Mic, Heart, Pause, ChevronDown, ChevronUp, Smartphone } from 'lucide-react';

const DailyDevotionals = () => {
  const { API } = useContext(AuthContext);
  const [reading, setReading] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [loadingReading, setLoadingReading] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [error, setError] = useState(null);
  
  // Lectio Divina state
  const [lectioData, setLectioData] = useState(null);
  const [loadingLectio, setLoadingLectio] = useState(false);
  const [expandedSection, setExpandedSection] = useState('pause');

  useEffect(() => {
    fetchTodaysReading();
    fetchLectioDivina();
  }, []);

  const fetchTodaysReading = async () => {
    setLoadingReading(true);
    setLoadingQuestions(true);
    setError(null);
    
    try {
      // Fetch reading info first (fast)
      const readingResponse = await axios.get(`${API}/devotional/reading`);
      setReading(readingResponse.data);
      setLoadingReading(false);
      
      // Then fetch questions (may be slower due to AI)
      try {
        const questionsResponse = await axios.get(`${API}/devotional/questions`);
        setQuestions(questionsResponse.data);
      } catch (qErr) {
        console.error('Error fetching questions:', qErr);
        // Use fallback questions
        setQuestions({
          questions: [
            `What does ${readingResponse.data.reference} reveal about God's character?`,
            "How does this passage connect to God's overarching story of redemption?",
            "What is the Holy Spirit speaking to you through this passage today?"
          ],
          application_questions: [
            "How can you apply what you've learned from this passage today?",
            "Who in your life needs to hear about what God is teaching you?"
          ]
        });
      }
      setLoadingQuestions(false);
    } catch (err) {
      console.error('Error fetching reading:', err);
      setError('Failed to load today\'s reading. Please try again.');
      setLoadingReading(false);
      setLoadingQuestions(false);
    }
  };

  const fetchLectioDivina = async () => {
    setLoadingLectio(true);
    try {
      const response = await axios.get(`${API}/devotional/lectio-divina`);
      setLectioData(response.data);
    } catch (err) {
      console.error('Error fetching Lectio Divina:', err);
    } finally {
      setLoadingLectio(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-8 animate-fade-in text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#2B6B7F] mb-3">
            Daily Devotionals
          </h1>
          <p className="text-slate-600">Nourish your soul with daily scripture and reflection</p>
        </div>

        {/* Tabs for Devotional Content */}
        <Tabs defaultValue="reading" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 h-auto gap-1">
            <TabsTrigger value="reading" className="flex flex-col items-center gap-1 py-3 px-2 h-auto text-center">
              <BookOpen className="w-5 h-5 flex-shrink-0 text-blue-500" />
              <span className="text-xs sm:text-sm leading-tight">Bible Reading<br/>Plan</span>
            </TabsTrigger>
            <TabsTrigger value="lectio" className="flex flex-col items-center gap-1 py-3 px-2 h-auto text-center" data-testid="lectio-tab">
              <Heart className="w-5 h-5 flex-shrink-0 text-purple-500" />
              <span className="text-xs sm:text-sm leading-tight">Lectio<br/>365</span>
            </TabsTrigger>
            <TabsTrigger value="wakeup" className="flex flex-col items-center gap-1 py-3 px-2 h-auto text-center">
              <Youtube className="w-5 h-5 flex-shrink-0 text-red-500" />
              <span className="text-xs sm:text-sm leading-tight">Wake Up<br/>Call</span>
            </TabsTrigger>
            <TabsTrigger value="podcast" className="flex flex-col items-center gap-1 py-3 px-2 h-auto text-center">
              <Mic className="w-5 h-5 flex-shrink-0 text-green-500" />
              <span className="text-xs sm:text-sm leading-tight">Podcast</span>
            </TabsTrigger>
          </TabsList>

          {/* Bible Reading Plan Tab */}
          <TabsContent value="reading">
            <Card className="border-t-4 border-t-blue-500">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                      2-Year Bible Reading Plan
                    </CardTitle>
                    <CardDescription>
                      Journey through the entire Bible with daily readings and reflection questions
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchTodaysReading}
                    disabled={loadingReading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingReading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingReading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#2B6B7F]" />
                    <span className="ml-3 text-slate-600">Loading today's reading...</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <p className="text-red-600 mb-4">{error}</p>
                    <Button onClick={fetchTodaysReading}>Try Again</Button>
                  </div>
                ) : reading ? (
                  <div className="space-y-6">
                    {/* Today's Reading Header */}
                    <div className="p-6 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
                      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                        <div>
                          <Badge className="bg-blue-600 mb-2">Day {reading.day_number} of 730</Badge>
                          <h3 className="text-2xl font-bold text-slate-800">{reading.reference}</h3>
                          <p className="text-slate-600">{reading.book} • Chapter {reading.chapter}</p>
                        </div>
                        <a
                          href={reading.bible_gateway_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Read on Bible Gateway
                        </a>
                      </div>
                      
                      {/* Embedded Scripture Reader */}
                      <div className="mt-4 bg-white rounded-lg border border-slate-200 overflow-hidden">
                        <iframe
                          src={`https://www.biblegateway.com/passage/?search=${encodeURIComponent(reading.reference)}&version=NIV&interface=print`}
                          title={`Read ${reading.reference}`}
                          className="w-full border-0"
                          height="400"
                          loading="lazy"
                        />
                      </div>
                    </div>

                    {/* Reflection Questions */}
                    <div className="p-6 rounded-lg bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200">
                      <h4 className="font-bold text-lg text-amber-800 mb-4 flex items-center gap-2">
                        <span className="w-8 h-8 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm">💭</span>
                        Reflection Questions
                      </h4>
                      <p className="text-sm text-slate-600 mb-4 italic">
                        Ponder these questions as you read. Consider how God might be speaking to you through this passage.
                      </p>
                      {loadingQuestions ? (
                        <div className="flex items-center gap-2 text-amber-700">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Generating thoughtful questions...</span>
                        </div>
                      ) : questions ? (
                        <ul className="space-y-4">
                          {questions.questions.map((question, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="w-6 h-6 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                {index + 1}
                              </span>
                              <p className="text-slate-700">{question}</p>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>

                    {/* Application Questions */}
                    <div className="p-6 rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200">
                      <h4 className="font-bold text-lg text-green-800 mb-4 flex items-center gap-2">
                        <span className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm">🎯</span>
                        Practical Application
                      </h4>
                      <p className="text-sm text-slate-600 mb-4 italic">
                        Consider how you can live out this passage today.
                      </p>
                      {loadingQuestions ? (
                        <div className="flex items-center gap-2 text-green-700">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Loading application questions...</span>
                        </div>
                      ) : questions ? (
                        <ul className="space-y-4">
                          {questions.application_questions.map((question, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="w-6 h-6 rounded-full bg-green-200 text-green-800 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                {index + 1}
                              </span>
                              <p className="text-slate-700">{question}</p>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>

                    {/* Wesleyan Note */}
                    <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                      <p className="text-sm text-purple-800">
                        <strong>Wesleyan Perspective:</strong> As you reflect, consider how God's prevenient grace has been working in your life even before you knew it, 
                        how justifying grace saves you through faith, and how sanctifying grace is transforming you into the image of Christ. 
                        Ask: "How is God calling me to grow in holiness and love today?"
                      </p>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lectio 365 Tab */}
          <TabsContent value="lectio">
            <Card className="border-t-4 border-t-purple-500">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                      Lectio Divina
                    </CardTitle>
                    <CardDescription>
                      Ancient practice of prayerful Bible reading - Pause, Rejoice, Ask, Yield
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={fetchLectioDivina}
                    disabled={loadingLectio}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingLectio ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingLectio ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                    <span className="ml-3 text-slate-600">Loading today's devotional...</span>
                  </div>
                ) : lectioData ? (
                  <div className="space-y-6">
                    {/* Today's Theme & Scripture */}
                    <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
                      <p className="text-sm text-purple-600 mb-1">{lectioData.date}</p>
                      <h3 className="text-xl font-bold text-purple-800 mb-2">{lectioData.theme}</h3>
                      <p className="text-lg font-semibold text-[#2B6B7F]">{lectioData.reference}</p>
                      <a
                        href={lectioData.bible_gateway_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-3 text-sm text-purple-600 hover:text-purple-800"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Read in Bible Gateway
                      </a>
                    </div>

                    {/* Scripture Text */}
                    {lectioData.scripture_text && (
                      <div className="p-5 bg-white border border-slate-200 rounded-lg shadow-sm">
                        <p className="text-slate-700 leading-relaxed italic text-center">
                          "{lectioData.scripture_text}"
                        </p>
                      </div>
                    )}

                    {/* PRAY Sections */}
                    <div className="space-y-3">
                      {['pause', 'rejoice', 'ask', 'yield'].map((section) => {
                        const data = lectioData.pray_prompts[section];
                        const isExpanded = expandedSection === section;
                        const colors = {
                          pause: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', accent: 'bg-blue-600' },
                          rejoice: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', accent: 'bg-amber-600' },
                          ask: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', accent: 'bg-green-600' },
                          yield: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', accent: 'bg-purple-600' }
                        };
                        const c = colors[section];
                        
                        return (
                          <div key={section} className={`rounded-lg border ${c.border} overflow-hidden`}>
                            <button
                              onClick={() => setExpandedSection(isExpanded ? null : section)}
                              className={`w-full p-4 ${c.bg} flex items-center justify-between hover:opacity-90 transition-opacity`}
                            >
                              <div className="flex items-center gap-3">
                                <span className={`w-10 h-10 rounded-full ${c.accent} text-white flex items-center justify-center text-xl`}>
                                  {data.icon}
                                </span>
                                <div className="text-left">
                                  <h4 className={`font-bold ${c.text}`}>{data.title}</h4>
                                  <p className="text-sm text-slate-600">{data.instruction}</p>
                                </div>
                              </div>
                              {isExpanded ? (
                                <ChevronUp className={`w-5 h-5 ${c.text}`} />
                              ) : (
                                <ChevronDown className={`w-5 h-5 ${c.text}`} />
                              )}
                            </button>
                            {isExpanded && (
                              <div className="p-4 bg-white border-t border-slate-100">
                                <ul className="space-y-3">
                                  {data.prompts.map((prompt, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                      <span className={`w-6 h-6 rounded-full ${c.bg} ${c.text} flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5`}>
                                        {idx + 1}
                                      </span>
                                      <p className="text-slate-700">{prompt}</p>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Closing Prayer */}
                    <div className="p-5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                      <h4 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
                        🙏 Closing Prayer
                      </h4>
                      <p className="text-slate-600 italic">{lectioData.closing_prayer}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    Unable to load devotional. Please try refreshing.
                  </div>
                )}

                {/* Official Lectio 365 Links */}
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <div className="text-center mb-4">
                    <h4 className="font-bold text-[#2B6B7F] flex items-center justify-center gap-2">
                      <Smartphone className="w-5 h-5" />
                      Official Lectio 365 App
                    </h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Get the full experience with audio devotionals from 24-7 Prayer
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <a
                      href="https://lectio365.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Visit Website
                    </a>
                    <a
                      href="https://apps.apple.com/app/lectio-365/id1479427447"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 transition-colors text-sm"
                    >
                      App Store
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.mustardseedapps.lectio365"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      Google Play
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Wake Up Call Tab */}
          <TabsContent value="wakeup">
            <Card className="border-t-4 border-t-red-500">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                      Seedbed Wake Up Call
                    </CardTitle>
                    <CardDescription>
                      Daily video devotionals to help you engage with God's Word each morning
                    </CardDescription>
                  </div>
                  <a
                    href="https://www.youtube.com/playlist?list=PLwfXuD2gBNyr5pmbkXNpb9c3eO6aI42Rh"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                  >
                    <Youtube className="w-4 h-4" />
                    Watch on YouTube
                  </a>
                </div>
              </CardHeader>
              <CardContent>
                <div className="aspect-video w-full rounded-lg overflow-hidden mb-4 bg-slate-900">
                  <iframe 
                    width="100%" 
                    height="100%"
                    src="https://www.youtube-nocookie.com/embed/videoseries?list=PLwfXuD2gBNyr5pmbkXNpb9c3eO6aI42Rh&rel=0&modestbranding=1" 
                    title="Seedbed Wake Up Call" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                    referrerPolicy="strict-origin-when-cross-origin" 
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 mb-2">
                    <strong>💡 Tip:</strong> If the video stops playing or shows an error, click the "Watch on YouTube" button above to continue watching directly on YouTube.
                  </p>
                </div>
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg mt-4">
                  <p className="text-sm text-slate-600 mb-2">
                    Watch the latest Wake Up Call videos from Seedbed for daily biblical encouragement.
                  </p>
                  <a
                    href="https://www.youtube.com/@WakeUpCall-Seedbed/streams"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View all streams on YouTube
                  </a>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Podcast Tab */}
          <TabsContent value="podcast">
            <Card className="border-t-4 border-t-green-500">
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                  Daily Devotional Podcast
                </CardTitle>
                <CardDescription>
                  Listen to daily encouragement and biblical teaching
                </CardDescription>
              </CardHeader>
              <CardContent>
                <iframe 
                  data-testid="embed-iframe" 
                  style={{ borderRadius: '12px' }}
                  src="https://open.spotify.com/embed/show/2aIqpc4mZVnWynKR0zlgk8?utm_source=generator" 
                  width="100%" 
                  height="352" 
                  frameBorder="0" 
                  allowFullScreen 
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
                  loading="lazy"
                  title="Daily Devotional Podcast"
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DailyDevotionals;
